import {Assert, Timer, TimerContext} from "js-vextensions";
import {PLACEHOLDER_TIMER_INTERVAL} from "../LibIntegrations/MobXGraphlink.js";

/**
 * A timer that fires based on "media time" — total elapsed time while audio is playing — rather
 * than real (wall-clock) time.  Provide a `getMediaTime` function (via SetMediaTimerOptions) that
 * returns the current media time in milliseconds; the timer only advances when that value advances.
 *
 * API is intentionally compatible with the js-vextensions `Timer` class:
 *   new MediaTimer(intervalMs, callback, maxCallCount?)
 *     .SetMediaTimerOptions(()=>comp.GetMediaTime())
 *     .SetContext(this.s.timerContext)
 *
 * maxCallCount = 1  → one-shot (fires once then stops; callback may safely re-arm the timer)
 * maxCallCount = -1 → repeating at baseIntervalMs of media time (default)
 */
export class MediaTimer {
	private readonly baseIntervalMs: number;
	private readonly callback: ()=>void;
	private readonly maxCallCount: number;

	private getMediaTimeImpl!: ()=>number;
	private targetMediaTime: number|null = null;
	private callCount_thisRun = 0;
	private readonly pollTimer: Timer;

	get Enabled() { return this.pollTimer.Enabled; }

	constructor(baseIntervalMs: number, callback: ()=>void, maxCallCount = -1) {
		this.baseIntervalMs = baseIntervalMs;
		this.callback = callback;
		this.maxCallCount = maxCallCount;
		// pollTimer is recreated in SetMediaTimerOptions; initialise with a dummy interval for now
		this.pollTimer = new Timer(PLACEHOLDER_TIMER_INTERVAL, ()=>this._Poll());
	}

	/** Configure the media-time source and (optionally) the poll interval (default 30 ms).
	 *  Must be called before Start(). */
	SetMediaTimerOptions(getMediaTime: ()=>number, pollIntervalMs = 30): this {
		Assert(!this.Enabled, "Media-timer-options must be set *prior* to timer being started.");
		this.getMediaTimeImpl = getMediaTime;
		this.pollTimer.intervalInMS = pollIntervalMs;
		return this;
	}

	/** Register the underlying poll timer with a TimerContext so it is stopped when the session ends. */
	SetContext(timerContext: TimerContext): this {
		this.pollTimer.SetContext(timerContext);
		return this;
	}

	/**
	 * Arm the timer. The callback fires after `firstIntervalMs` (or `baseIntervalMs` if omitted)
	 * of media time has elapsed.  For repeating timers, subsequent ticks are spaced by `baseIntervalMs`.
	 * Calling Start() while already running restarts the timer (same behaviour as Timer.Start()).
	 */
	Start(firstIntervalMs?: number): this {
		this.callCount_thisRun = 0;
		this.targetMediaTime = this.getMediaTimeImpl() + (firstIntervalMs ?? this.baseIntervalMs);
		if (!this.pollTimer.Enabled) this.pollTimer.Start();
		return this;
	}

	Stop(): this {
		this.pollTimer.Stop();
		this.targetMediaTime = null;
		return this;
	}

	private _Poll() {
		if (this.targetMediaTime == null) return;
		const mt = this.getMediaTimeImpl();
		if (mt < this.targetMediaTime) return;

		this.callCount_thisRun++;
		const isLastCall = this.maxCallCount !== -1 && this.callCount_thisRun >= this.maxCallCount;
		if (isLastCall) {
			// Stop before calling callback so the callback may safely re-arm the timer.
			this.targetMediaTime = null;
			this.pollTimer.Stop();
			this.callback();
		} else {
			// Repeating: advance past any missed ticks (e.g. after a long pause).
			while (this.targetMediaTime <= mt) {
				this.targetMediaTime += this.baseIntervalMs;
			}
			this.callback();
		}
	}
}